import dayjs from 'dayjs';
import jwtDecode from 'jwt-decode';
import loadImage from 'blueimp-load-image';
import VisaCardIcon from '@/components/icons/VisaCardIcon.vue';
import MasterCardIcon from '@/components/icons/MasterCardIcon.vue';
import AmexCardIcon from '@/components/icons/AmexCardIcon.vue';
import DiscoverCardIcon from '@/components/icons/DiscoverCardIcon.vue';
import DinnerClubCardIcon from '@/components/icons/DinnerClubCardIcon.vue';
import JCBCardIcon from '@/components/icons/JCBCardIcon.vue';
import UnionPayCardIcon from '@/components/icons/UnionPayCardIcon.vue';
import type { RouteRecordNameGeneric } from 'vue-router';
import type { LocationCoordinate } from '@/types/location';
import type { ROUTE_PARAMS, StringMap } from '@/types/common';

export const getLocalStorage = (key: string) => {
	const stringValue = localStorage.getItem(key) || '';
	try {
		return JSON.parse(stringValue);
	} catch {
		return stringValue;
	}
};

export const setLocalStorage = (key: string, data: string) => {
	localStorage.setItem(key, data);
};

export const removeLocalStorage = (key: string) => {
	localStorage.removeItem(key);
};

export const getSessionStorage = (key: string) => {
	const stringValue = sessionStorage.getItem(key) || '';
	try {
		return JSON.parse(stringValue);
	} catch {
		return stringValue;
	}
};

export const setSessionStorage = (key: string, data: string) => {
	sessionStorage.setItem(key, data);
};

export const removeSessionStorage = (key: string) => {
	sessionStorage.removeItem(key);
};

export const deepClone = (obj: any) => {
	return JSON.parse(JSON.stringify(obj));
};

export const generateRandomColor = (index: number) => {
	const baseSeed = 1000; // Adjust as needed for more randomness

	// Use the index combined with a base seed for added randomness
	let seed: number = index * baseSeed;

	// Use a simple random number generator to introduce more variability
	const random = () => (Math.sin(seed++) + 1) / 2;

	// Generate random values for the red, green, and blue components with a wider range
	const red = Math.floor(random() * 200) + 55;
	const green = Math.floor(random() * 200) + 55;
	const blue = Math.floor(random() * 200) + 55;

	// Convert the RGB components to a CSS color code
	const colorCode = `${red},${green},${blue}`;

	return colorCode;
};

export const fetchImageAsBlob = async (imagePath: string) => {
	const response = await fetch(imagePath);
	const blob = await response.blob();
	return blob;
};

export const openLinkInCurrentTab = (url: string) => {
	// Redirect to the specified URL
	window.location.href = url;
};

export const openLink = (url: string, target = '_blank') => {
	// Redirect to the specified URL
	window.open(url, target);
};

export const copyToClipboard = async (text: string) => {
	return navigator.clipboard.writeText(text);
};

/**
 * Generate a full URL path by combining the current origin with the given path.
 * If the path starts with a slash, it is preserved.
 * If the path already includes the subdomain (if set), the subdomain is not added.
 * The subdomain is formatted to ensure it has exactly one trailing slash.
 * The final path is created by avoiding double slashes.
 *
 * @param {string} path - The path to combine with the origin
 * @returns {string} The full URL path
 */
export const getFullUrlPath = (path: string) => {
	const currentOrigin = window.location.origin;
	const subDomain = import.meta.env.VITE_SUBDOMAIN || ''; // '/us

	// If the path already includes the subdomain, just return the full URL
	if (subDomain && path.includes(subDomain)) {
		if (path.startsWith('/user')) {
			return `${currentOrigin}${subDomain}${path}`;
		} else if (path.startsWith('/')) {
			// Make sure we don't have a double slash after the origin
			return `${currentOrigin}${path}`;
		}
		return `${currentOrigin}/${path}`;
	}

	// Format the subdomain with a trailing slash if it doesn't have one
	let formattedSubDomain = '/';
	if (subDomain) {
		formattedSubDomain = subDomain.startsWith('/') ? subDomain : `/${subDomain}`;
		formattedSubDomain = formattedSubDomain.endsWith('/') ? formattedSubDomain : `${formattedSubDomain}/`;
	}

	// Create the final URL, carefully handling slashes to avoid duplicates
	if (path.startsWith('/')) {
		// Remove the leading slash from the path to avoid double slash
		return `${currentOrigin}${formattedSubDomain}${path.substring(1)}`;
	}

	return `${currentOrigin}${formattedSubDomain}${path}`;
};

/**
 * Converts a snake_case or camelCase string to capital case.
 *
 * @param {string} str - The input string to be converted.
 * @returns {string} The converted string in capital case.
 * @example
 *
 * convertToCapitalCase('currentlyDue');
 * // Returns: "Currently Due"
 *
 * convertToCapitalCase('business_profile');
 * // Returns: "Business Profile"
 *
 * convertToCapitalCase('support_phone');
 * // Returns: "Support Phone"
 */
export const convertToCapitalCase = (str: string) => {
	// Handle snake_case
	const snakeCaseConverted = str.replace(/_/g, ' ');
	// Handle camelCase by inserting space before capital letters
	const camelCaseConverted = snakeCaseConverted.replace(/([A-Z])/g, ' $1');
	// Capitalize first letter of each word
	return camelCaseConverted.replace(/\b\w/g, (char) => char.toUpperCase());
};

// Utility function to remove spaces and convert to lowercase
export const normalize = (str: string) => {
	return str.replace(/\s+/g, '').toLowerCase();
};

export const isCardExpired = (expiryDate: string): boolean => {
	const currentDateTime = dayjs();
	const expiryDateTime = dayjs(expiryDate, 'MM/YYYY');

	return expiryDateTime.isBefore(currentDateTime, 'month');
};

export const getCreditCardIcon = (brand: string | undefined) => {
	switch (brand) {
		case CARD_BRAND.VISA:
			return VisaCardIcon;
		case CARD_BRAND.MASTERCARD:
			return MasterCardIcon;
		case CARD_BRAND.AMEX:
			return AmexCardIcon;
		case CARD_BRAND.DISCOVER:
			return DiscoverCardIcon;
		case CARD_BRAND.DINERS:
			return DinnerClubCardIcon;
		case CARD_BRAND.JCB:
			return JCBCardIcon;
		case CARD_BRAND.UNIONPAY:
			return UnionPayCardIcon;
		default:
			return VisaCardIcon;
	}
};

export const getSortDirection = (direction: string) => {
	switch (direction) {
		case TABLE_SORT_DIRECTION.ASC:
			return ARROW_ICON_DIRECTION.DOWN;
		case TABLE_SORT_DIRECTION.DESC:
			return ARROW_ICON_DIRECTION.UP;
		default:
			return '';
	}
};

export const getDefaultUser = (data: AppleSignInAPI.SignInResponseI) => {
	const decoded: { email: string } = jwtDecode(data.authorization.id_token);
	return {
		email: data.user?.email || decoded.email,
		name: {
			firstName: data.user?.name.firstName || 'Firstname',
			lastName: data.user?.name.lastName || 'Lastname',
		},
	};
};

export const replaceTextInCurlyBraces = <T>(text: string, replacementValues: T[]) => {
	return text.replace(
		REGEX_MATCH_CURLY_BRACES_PATTERN,
		(_, replacedText) => replacementValues[replacedText] || replacedText,
	);
};

export const getRouteTitleByName = (name: RouteRecordNameGeneric, routeObject: ROUTE_PARAMS = ROUTE) => {
	const routes = Object.values(routeObject);
	const title = 'Peatix';

	if (name) {
		const currentRoute = routes.find((route) => route.NAME === name);
		return currentRoute ? `${currentRoute.TITLE} | ${title}` : title;
	}
	return title;
};

export const getImageDimension = (imageUrl: string): Promise<{
	width: number
	height: number
}> => {
	return new Promise((resolve) => {
		const img = new Image();
		img.src = imageUrl;
		img.onload = () => {
			resolve({
				width: img.width,
				height: img.height,
			});
		};
	});
};

/**
 * Use https://github.com/blueimp/JavaScript-Load-Image to fix heif file rotate
 *
 * @param file File
 * @returns Promise<File | Blob>
 */
export const processImageFile = async (file: File, maxWidth: number): Promise<File | Blob> => {
	return new Promise((resolve) => {
		loadImage(
			file,
			(img, data) => {
				if (data && data.imageHead && data.exif) {
					// Rotate the file by override the Exif Orientation value
					loadImage.writeExifData(data.imageHead, data as loadImage.WriteExifData, 'Orientation', 1);
					const newImageFile = img as HTMLCanvasElement;
					// Convert canvas to Blob
					newImageFile.toBlob((blob) => {
						if (blob && data.imageHead) {
							loadImage.replaceHead(blob, data.imageHead, (newBlob) => {
								if (newBlob) {
									resolve(newBlob);
								}
							});
						}
					}, 'image/jpeg');
				} else {
					resolve(file);
				}
			},
			{
				meta: true,
				orientation: true,
				canvas: true,
				maxWidth,
			},
		);
	});
};

export const convertImageUrlToBlob = async (url: string) => {
	return await fetch(url).then((r) => r.blob());
};

export const getQueryParam = (key: string): string | null => {
	try {
		const url = new URL(window.location.href);
		return url.searchParams.get(key);
	} catch (error) {
		return null;
	}
};

export const getPluralSuffix = (num: number) => {
	return num > 1 ? 's' : '';
};

export const scrollToTop = (smooth: boolean = true) => {
	window.scrollTo({
		top: 0,
		behavior: smooth ? 'smooth' : 'auto',
	});
};

export const convertCoordinatesToString = (coordinates: LocationCoordinate | null) => {
	if (coordinates) {
		return `${coordinates.lat},${coordinates.lng}`;
	}
	return '';
};

export const convertStringToCoordinates = (coordinates: string) => {
	if (coordinates) {
		const [lat, lng] = String(coordinates).split(',').map(Number);
		return { lat, lng };
	}
	return '';
};

export const removeValueFromQuery = (
	query: StringMap,
	keysToRemove: string | string[],
	isforceRemove = false,
) => {
	const keys = Array.isArray(keysToRemove) ? keysToRemove : [keysToRemove];

	keys.forEach((key) => {
		if (isforceRemove || query[key] === '') {
			delete query[key];
		}
	});
	return query;
};
